import { LitElement, html, TemplateResult, css, unsafeCSS } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import mainStyles from './styles/main.scss?inline';
import { msg, localized, updateLocale } from './localization/config';
import { emitError, emitWarning } from './utils/form-associated';
import { LocaleCode } from './types/lang.types';
import './types/grecaptcha.types';
import type {
    AppointmentBookingData,
    AppointmentBookingFormData,
    LanguageMap,
    OpeningHour,
    OpeningHours,
    SalesSiteResponse,
    SpecialOpeningHour,
    Variant,
} from './types/sales-site.types';
import type { FormAlert } from './types/appointment-booking.types';
import { DEFAULT_OPENING_HOURS } from './opening-hours.defaults';
import './components/date-picker';
import './components/time-picker';
import './components/request-form';
import './components/selected-data';
import DatePicker from './components/date-picker';
import TimePicker from './components/time-picker';
import RequestForm from './components/request-form';
import { DateUtility } from './components/calendar/utils/date-utility';
import { formatRichText } from './utils/format-richt-text';
import { formatDate, formatDateToISO, parseExpert } from './utils/helper';
import {
    registerIcons,
    systemPin,
    systemCalendar,
    systemClock,
    systemAttentionFull,
} from '@dertour/ui-components/icons';

registerIcons({ systemPin, systemCalendar, systemClock, systemAttentionFull });

/**
 *
 */
@localized()
export class AppointmentBooking extends LitElement {
    static override styles = [
        css`
            ${unsafeCSS(mainStyles)}
        `,
    ];

    @property({ type: String })
    public lang = LocaleCode.DE_CH;

    @property({ type: String })
    public currency = '';

    @property({ type: String })
    public brand = '';

    @property({ type: String })
    public tenant = '';

    @property({ type: Object, attribute: 'additional-tracking-data' })
    public additionalTrackingData = {};

    @property({ type: Number, attribute: 'booking-day-offset' })
    bookingDayOffset = 3;

    @property({ type: String, attribute: 'contact-language' })
    contactLanguage = '';

    @property({ type: String, attribute: 'form-name' })
    formName = 'appointmentBookingForm';

    @property({ type: Boolean, attribute: 'hide-module-title' })
    hideModuleTitle = false;

    @property({ type: String, attribute: 'opportunity-description' })
    opportunityDescription = '';

    @property({ type: String, attribute: 'opportunity-destination' })
    opportunityDestination = '';

    @property({ type: String, attribute: 'opportunity-source' })
    opportunitySource = '948150003';

    @property({ type: String, attribute: 'opportunity-source-url' })
    opportunitySourceUrl = '';

    @property({ type: String, attribute: 'opportunity-title' })
    opportunityTitle = '';

    @property({ type: String, attribute: 'opportunity-type' })
    opportunityType = '';

    @property({ type: String, attribute: 'opportunity-owning-team' })
    opportunityOwningTeam = '';

    @property({ type: String, attribute: 'opportunity-product-brand' })
    opportunityProductBrand = '';

    @property({ type: String, attribute: 'opportunity-product-id' })
    opportunityProductId = '';

    @property({ type: String, attribute: 'variant' })
    variant: Variant = 'default';

    @property({ type: String, attribute: 'newsletter' })
    newsletter = '';

    @property({ type: String, attribute: 'expert' })
    expert = '';

    @property({ type: String, attribute: 'data-protection-link' })
    dataProtectionLink = '';

    @property({ type: String, attribute: 'site-key' })
    siteKey = '6LfTcM0sAAAAAIaj397Q7vrXWKW7EDD77lAihqHU';

    @property({ type: Boolean, attribute: 'enable-recap' })
    enableRecap: boolean = false;

    // Important: do NOT change this value — analytics tracking depends on the exact string 'der-tailormade-appointment-booking'
    opportunityOriginatingObjectTag = 'der-tailormade-appointment-booking';

    @state()
    openingHours: OpeningHours = DEFAULT_OPENING_HOURS;

    @state()
    openingWeekdays: number[] = [];

    @state()
    specialSelectableDates: string[] = [];

    @state()
    salesSite: SalesSiteResponse = {} as SalesSiteResponse;

    @state()
    showConfirmation: boolean = false;

    @state()
    selectionData: AppointmentBookingFormData = {} as AppointmentBookingFormData;

    @state()
    salesSiteIdentifier: string | undefined = undefined;

    @state()
    private isSubmitting = false;

    @state()
    protected grecaptchaError = false;

    @state()
    protected crmError = false;

    get formAlert(): FormAlert | undefined {
        if (this.grecaptchaError) {
            return { type: 'error', message: msg('grecaptchaError') };
        }
        if (this.crmError) {
            return { type: 'warning', message: msg('crmError') };
        }
        return undefined;
    }

    private _currentBrand: string | null = null;
    private _salesSiteRequestSeq = 0;

    private get isRecaptchaEnabled(): boolean {
        return this.enableRecap;
    }

    @query('der-ui-progress-bar') progressBarComponent!: HTMLElement & { showNextItem(): void };
    @query('#date-form') dateForm!: HTMLFormElement;
    @query('der-tm-appointment-booking-date-picker') datePickerComponent!: DatePicker;
    @query('der-tm-appointment-booking-time-picker') timePickerComponent!: TimePicker;
    @query('der-tm-appointment-booking-request-form') requestComponent!: RequestForm;
    @query('.m-appointment-booking__confirmation') private _confirmationElement?: HTMLElement;

    /**
     * Resolves the sales-sites facade URL from dev env or CI placeholder.
     * Pattern: dtch-tailormade-appointment-booking-{env}.azurewebsites.net/api/get-salessites
     */
    private _getSalesSitesBaseUrl(): URL {
        const base = `${import.meta.env.VITE_SALES_SITES_URL ?? ''}`;
        return new URL(base);
    }

    /**
     * Resolves the booking submit URL from dev env or CI placeholder.
     * Pattern: dtch-tailormade-appointment-booking-{env}.azurewebsites.net/api/appointment-booking-send-data
     */
    private _getSubmitUrl(): string {
        return `${import.meta.env.VITE_APPOINTMENT_BOOKING_URL ?? ''}`;
    }

    private async _getSalesSiteById(): Promise<boolean> {
        const teamId = this.salesSiteIdentifier ?? '';

        if (!teamId) {
            return false;
        }

        const requestSeq = ++this._salesSiteRequestSeq;
        const url = this._getSalesSitesBaseUrl();
        url.searchParams.set('teamId', teamId);
        url.searchParams.set('lang', this.lang ?? '');
        url.searchParams.set('brand', this.brand ?? '');
        url.searchParams.set('tenant', this.tenant ?? '');

        const response = await fetch(url.toString());

        if (!response.ok) {
            if (requestSeq !== this._salesSiteRequestSeq || this.salesSiteIdentifier !== teamId) {
                return false;
            }

            this.error(this.tagName, {
                message: 'Can not load sales-site data',
                teamId,
            });
            return false;
        }

        const data = (await response.json()) as SalesSiteResponse;
        if (requestSeq !== this._salesSiteRequestSeq || this.salesSiteIdentifier !== teamId) {
            return false;
        }

        this.salesSite = data;
        return true;
    }

    override async connectedCallback(): Promise<void> {
        super.connectedCallback();
        await updateLocale(this.lang ?? LocaleCode.DE_CH);
        this._currentBrand = this.brand || null;

        if (this.brand) {
            console.log(`📌 Brand attribute found: ${this.brand}`);
            this._currentBrand = this.brand;
        } else {
            console.warn('⚠️ No brand attribute found on component');
        }

        if (this.isRecaptchaEnabled) {
            if (typeof window.grecaptcha === 'undefined' || !window.grecaptcha.enterprise) {
                this.grecaptchaError = true;
            }
        } else {
            this.grecaptchaError = false;
        }
    }

    override async updated(changedProperties: Map<string | number | symbol, unknown>): Promise<void> {
        super.updated(changedProperties);

        if (changedProperties.has('lang')) {
            await updateLocale(this.lang ?? LocaleCode.DE_CH);
        }
        if (changedProperties.has('opportunityOwningTeam') && this.opportunityOwningTeam) {
            this.salesSiteIdentifier = this.opportunityOwningTeam;
            this.selectionData.OpportunityOwningTeam = this.opportunityOwningTeam;

            const didApplySalesSite = await this._getSalesSiteById();
            if (!didApplySalesSite) {
                return;
            }

            this._setOpeningWeekdays();
            this._setOpeningHours();
            this._setSpecialSelectableDates();
        }
        // Handle brand changes at runtime
        if (changedProperties.has('brand') && this.brand !== this._currentBrand) {
            this._currentBrand = this.brand;
        }

        if (this.salesSiteIdentifier) {
            if (changedProperties.has('salesSiteIdentifier')) {
                const didApplySalesSite = await this._getSalesSiteById();
                if (!didApplySalesSite) {
                    return;
                }
                this._setOpeningWeekdays();
                this._setOpeningHours();
                this._setSpecialSelectableDates();
            }
        }
    }

    /**
     * Emit a warning event
     * @param name - The warning name
     * @param data - Additional warning data
     */
    protected warn(name: string, data: object): void {
        emitWarning(this, name, data);
    }

    /**
     * Emit an error event
     * @param name - The error name
     * @param data - Additional error data
     */
    protected error(name: string, data: object): void {
        emitError(this, name, data);
    }

    private _assignValue(obj: Partial<AppointmentBookingFormData>): void {
        Object.assign(this.selectionData, obj);
        this.requestUpdate();
    }

    /**
     * Sets available opening days based on current
     * sales branch opening hours.
     *
     * @private
     */
    private _setOpeningWeekdays(): void {
        if (!this.salesSite.openingHours) {
            this.openingWeekdays = [1, 2, 3, 4, 5, 6];
            // Clear owning team if branch has no opening hours
            this._assignValue({ OpportunityOwningTeam: '' });
            return;
        }
        this.openingWeekdays = this.salesSite.openingHours.defaultOpeningHours.map(
            (entry: OpeningHour): number => entry.weekday
        );
    }

    private _setOpeningHours(): void {
        if (!this.salesSite.openingHours) {
            return;
        }

        this.openingHours = this.salesSite.openingHours;
    }

    /**
     * Sets special selectable dates from special opening hours.
     * Only includes dates where isOpen is true.
     *
     * @private
     */
    private _setSpecialSelectableDates(): void {
        if (!this.salesSite?.openingHours?.specialOpeningHours) {
            this.specialSelectableDates = [];
            return;
        }

        this.specialSelectableDates = this.salesSite.openingHours.specialOpeningHours
            .filter((special: SpecialOpeningHour) => special.isOpen)
            .map((special: SpecialOpeningHour) => {
                try {
                    const normalizedDate = DateUtility.getDateString(special.date);
                    if (DateUtility.isValidDateString(normalizedDate)) {
                        return normalizedDate;
                    }
                    return null;
                } catch (error) {
                    console.warn('Error parsing special opening hour date:', special.date, error);
                    return null;
                }
            })
            .filter((date: string | null): date is string => date !== null);
    }

    private _setOpportunitySourceUrl(): void {
        this.opportunitySourceUrl = this.opportunitySourceUrl || (top || window)?.location?.href || '';
    }

    private _scrollToConfirmation(): void {
        this.updateComplete.then(() => {
            if (this._confirmationElement) {
                this._confirmationElement.scrollIntoView({ behavior: 'smooth' });
            }
        });
    }

    private _isDateFormValid(): boolean {
        if (!this.datePickerComponent.validate()) {
            return false;
        }

        this._assignValue(this.datePickerComponent.getFormValue());
        return true;
    }

    private _isTimePickerValid(): boolean {
        if (!this.timePickerComponent.validate()) {
            return false;
        }

        this._assignValue({ OpportunityConsultationTime: this.timePickerComponent.selectedTimeSlot });

        return true;
    }

    private _setContactLanguage(): void {
        const languageMap: LanguageMap = {
            'fr-CH': '948150001',
            'de-CH': '948150000',
        };
        this.contactLanguage = this.contactLanguage || languageMap[this.lang] || '';
    }

    /**
     * Requests marketing parameters (UTM + GCLID) via BroadcastChannel.
     * Resolves with the parameters if received, or rejects if no response within the timeout period.
     * @param timeout Timeout in ms after which the promise rejects
     */
    private _requestMarketingParams(timeout = 1000): Promise<{ utmParams: string | null; gclidParams: string | null }> {
        return new Promise((resolve, reject) => {
            const channel = new BroadcastChannel('DER-TM-MFE-DATA-CHANNEL');
            const handler = (event: MessageEvent) => {
                const data = event.data;
                if (data && data.type === 'responseParams') {
                    channel.removeEventListener('message', handler);
                    channel.close();
                    resolve({ utmParams: data.utmParams, gclidParams: data.gclidParams });
                }
            };
            channel.addEventListener('message', handler);

            channel.postMessage({ type: 'requestParams' });

            setTimeout(() => {
                channel.removeEventListener('message', handler);
                channel.close();
                reject(new Error('Timeout: No response received'));
            }, timeout);
        });
    }

    /**
     * Parses the expert attribute and returns the CRM payload fields
     * Expert (id) and ExpertName (name) if both values are set.
     */
    private _getExpertData(): { Expert?: string; ExpertName?: string } {
        const expert = parseExpert(this.expert);
        if (!expert) {
            return {};
        }
        return { Expert: expert.id, ExpertName: expert.name };
    }

    /**
     * Collects and combines all appointment booking data into a single object.
     *
     * Gathers both user input data (from forms and pickers) and internal configuration
     * data to create a complete appointment booking dataset. This data will be used for
     * submission to the server and analytics events.
     *
     * @private
     * @returns {AppointmentBookingData} Complete appointment booking data object with user inputs and metadata
     *
     * @throws {Error} If selection data or additional data cannot be retrieved
     */
    private _getAppointmentBookingData(isNewsletterChecked = false): AppointmentBookingData {
        this._setOpportunitySourceUrl();
        this._setContactLanguage();
        const opportunityOwningTeam = this.selectionData.OpportunityOwningTeam ?? '';

        const additionalData: Omit<AppointmentBookingData, keyof AppointmentBookingFormData> = {
            formName: this.formName,
            ContactLanguage: this.contactLanguage,
            OpportunitySource: this.opportunitySource,
            OpportunitySourceURL: this.opportunitySourceUrl,
            OpportunityType: this.opportunityType,
            OpportunityProductBrand: this.opportunityProductBrand,
            OpportunityOriginatingObjectTag: this.opportunityOriginatingObjectTag,
            variant: this.variant,
            Brand: this.brand,
            Tenant: this.tenant,
            ContactNewsletterOptIn: isNewsletterChecked ? (this.newsletter ?? '').trim() : '',
            ...this._getExpertData(),
        };

        return {
            ...this.selectionData,
            OpportunityOwningTeam: opportunityOwningTeam,
            ...additionalData,
        };
    }

    /**
     * Dispatches a custom analytics event to track appointment booking completion.
     *
     * Sends a composed and bubbling CustomEvent containing detailed booking information
     * to enable analytics tracking and measurement of booking completion. This event
     * is dispatched after successful form submission to capture user interaction data.
     *
     * @private
     * @param {AppointmentBookingData} data - Complete appointment booking data object
     * @emits der-analytics CustomEvent with detailed booking analytics data
     *
     */
    private _dispatchAnalyticsEvent(data: AppointmentBookingData): void {
        const reducedAnalyticsData = {
            selectedDate: formatDateToISO(data.OpportunityConsultationDate),
            selectedTime: data.OpportunityConsultationTime,
            email: data.ContactEmail,
        };

        this.dispatchEvent(
            new CustomEvent('der-analytics', {
                detail: {
                    name: 'form-submitted',
                    element_name: this.tagName.toLowerCase(),
                    element_id: this.id ?? '',
                    action: 'confirmation view after form submit',
                    category: '',
                    data: reducedAnalyticsData,
                },
                bubbles: true,
                composed: true,
            })
        );
    }

    private async _handleSubmitClick(): Promise<boolean> {
        if (this.showConfirmation || this.isSubmitting || !this.requestComponent.validate()) {
            return false;
        }

        // Mark submission started to prevent double clicks
        this.isSubmitting = true;

        try {
            let reCaptchaToken = '';

            if (this.isRecaptchaEnabled) {
                if (typeof window.grecaptcha !== 'undefined' && window.grecaptcha.enterprise) {
                    try {
                        reCaptchaToken = await window.grecaptcha.enterprise.execute(this.siteKey, {
                            action: 'der_tm_appointment_booking',
                        });
                        this.grecaptchaError = false;
                    } catch {
                        this.grecaptchaError = true;
                        this.isSubmitting = false;
                        return false;
                    }
                } else {
                    this.grecaptchaError = true;
                    this.isSubmitting = false;
                    return false;
                }
            } else {
                this.grecaptchaError = false;
            }

            const formValues = this.requestComponent.getFormValue() as Record<string, unknown>;
            const isNewsletterChecked = 'ContactNewsletter' in formValues;
            delete formValues['ContactNewsletter'];
            this._assignValue(formValues as Partial<AppointmentBookingFormData>);

            // Direct confirmation path (no network call)
            if (this.selectionData.ContactEmailConfirmation) {
                this.showConfirmation = true;
                this._scrollToConfirmation();
                // Safe to unlock immediately (synchronous path)
                this.isSubmitting = false;
            } else {
                const data: AppointmentBookingData = this._getAppointmentBookingData(isNewsletterChecked);

                // Fetch marketing params (UTM + GCLID) via BroadcastChannel
                let marketingParams = { utmParams: null as unknown, gclidParams: null as unknown };
                try {
                    const tryParseJson = (value: unknown): unknown => {
                        try {
                            return typeof value === 'string' ? JSON.parse(value) : value;
                        } catch {
                            return value;
                        }
                    };
                    const raw = await this._requestMarketingParams();
                    marketingParams = {
                        utmParams: tryParseJson(raw.utmParams),
                        gclidParams: tryParseJson(raw.gclidParams),
                    };
                } catch {
                    /* no marketing params available */
                }

                let utmEntries: Record<string, unknown> = {};
                if (marketingParams.utmParams && typeof marketingParams.utmParams === 'object') {
                    utmEntries = Object.entries(marketingParams.utmParams as Record<string, unknown>).reduce(
                        (acc, [key, value]) => {
                            const stripped = key.replace(/^utm_/, '');
                            const propName = `OpportunityUtm${stripped.charAt(0).toUpperCase()}${stripped.slice(1)}`;
                            acc[propName] = value;
                            return acc;
                        },
                        {} as Record<string, unknown>
                    );
                }

                const gclidEntries =
                    marketingParams.gclidParams && typeof marketingParams.gclidParams === 'object'
                        ? Object.entries(marketingParams.gclidParams as Record<string, unknown>).reduce(
                              (acc, [key, value]) => {
                                  const propName =
                                      key.toLowerCase() === 'gclid'
                                          ? 'OpportunityGclid'
                                          : `OpportunityGclid${key.charAt(0).toUpperCase()}${key.slice(1)}`;
                                  acc[propName] = value;
                                  return acc;
                              },
                              {} as Record<string, unknown>
                          )
                        : {};

                const recaptchaData = this.isRecaptchaEnabled
                    ? {
                          reCaptchaToken,
                          reCaptchaSiteKey: this.siteKey,
                      }
                    : {};

                const dataWithMarketing = { ...data, ...utmEntries, ...gclidEntries, ...recaptchaData };

                this.dispatchEvent(
                    new CustomEvent('der-analytics', {
                        detail: {
                            name: 'form-submit',
                            element_name: this.tagName.toLowerCase(),
                            element_id: this.id ?? '',
                            variant: this.variant,
                            action: 'form submit clicked',
                            category: 'form',
                            data: dataWithMarketing,
                        },
                        bubbles: true,
                        composed: true,
                    })
                );

                fetch(this._getSubmitUrl(), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(dataWithMarketing),
                })
                    .then(async (response: Response): Promise<void> => {
                        if (response.ok) {
                            this.crmError = false;
                            let scoreTooLow = false;
                            try {
                                const body = await response.json();
                                scoreTooLow = !!body?.scoreTooLow;
                            } catch {
                                // response body is not valid JSON – treat as successful submission
                            }
                            this.showConfirmation = true;
                            this._scrollToConfirmation();
                            if (!scoreTooLow) {
                                this._dispatchAnalyticsEvent(data);
                            }
                        } else {
                            this.crmError = true;
                        }
                    })
                    .catch((error: unknown) => {
                        console.error('Error in form submission:', error);
                        this.crmError = true;
                    })
                    .finally(() => {
                        // Unlock after async completes
                        this.isSubmitting = false;
                    });
            }
        } catch (error) {
            console.error('Error in form submission:', error);
            // In case of synchronous error unlock again
            this.isSubmitting = false;
        }
        return false;
    }

    /**
     * Handles the selection of an appointment date in the DatePicker component.
     * Validates the date and proceeds to next booking step if valid.
     *
     * @param event CustomEvent with selected date and booking data
     */
    private _handleDatePickerSelection(event: CustomEvent): void {
        event.stopPropagation();
        if (event.detail.value && this._isDateFormValid()) {
            // Reset time picker when date changes
            this.timePickerComponent.reset();
            this.progressBarComponent.showNextItem();
        }
    }

    /**
     * Handles the selection of a new sales branch.
     * Resets relevant state values and triggers re-initialization
     * of components.
     *
     * @param event CustomEvent with ID of selected sales branch
     */
    private _handleSalesSiteChange(event: CustomEvent<{ salesSite: string }>): void {
        event.stopPropagation();
        this.salesSiteIdentifier = event.detail.salesSite;
        this._assignValue({ OpportunityOwningTeam: event.detail.salesSite });
        this.openingWeekdays = [];
        if (!event.detail.salesSite) {
            this.salesSite = {} as SalesSiteResponse;
            this.openingHours = DEFAULT_OPENING_HOURS;
            this.specialSelectableDates = [];
        }
        this.datePickerComponent?.reset();
        this.timePickerComponent?.reset();
    }

    private _handleConsultationChannelChange(event: CustomEvent<{ isSalesSiteRequired: boolean }>): void {
        event.stopPropagation();
        if (this.opportunityOwningTeam) {
            this.salesSiteIdentifier = this.opportunityOwningTeam;
            return;
        }

        this.salesSiteIdentifier = undefined;
        this.salesSite = {} as SalesSiteResponse;
        this._assignValue({ OpportunityOwningTeam: '' });
        this.specialSelectableDates = [];
        this.openingHours = DEFAULT_OPENING_HOURS;
        this.datePickerComponent?.reset();
        this.timePickerComponent?.reset();
    }

    private _handleTimePickerSelection(event: CustomEvent): void {
        event.stopPropagation();
        if (this._isTimePickerValid()) {
            this.progressBarComponent.showNextItem();
        }
    }

    override render(): TemplateResult {
        return html`
            <div class="m-appointment-booking">
                <div class="m-appointment-booking__content">
                    ${
                        !this.hideModuleTitle
                            ? html`
                                  <div class="m-appointment-booking__information">
                                      <der-ui-module-title
                                          title="${msg('businessComponent.title')}"
                                          intro="${msg('businessComponent.intro')}"
                                          ?inverted=${true}
                                      ></der-ui-module-title>
                                  </div>
                              `
                            : ''
                    }
                    <div class="m-appointment-booking__progress-bar">
                        ${
                            !this.showConfirmation
                                ? html` <div class="m-appointment-booking__progress-bar-wrapper">
                                      <der-ui-progress-bar lang=${this.lang} .disabledButton=${this.grecaptchaError}>
                                          <der-ui-progress-bar-item
                                              label="${msg('datepicker.title')}"
                                              .nextButton=${{
                                                  type: 'link',
                                                  clickFn: () => this._isDateFormValid(),
                                              }}
                                          >
                                              <der-tm-appointment-booking-date-picker
                                                  lang="${this.lang}"
                                                  brand="${this.brand}"
                                                  tenant="${this.tenant}"
                                                  booking-day-offset="${this.bookingDayOffset}"
                                                  opportunity-owning-team="${ifDefined(this.opportunityOwningTeam)}"
                                                  .openingWeekdays=${this.openingWeekdays}
                                                  .specialSelectableDates=${this.specialSelectableDates}
                                                  @calendar-date-selected=${this._handleDatePickerSelection}
                                                  @consultation-channel-changed=${this._handleConsultationChannelChange}
                                                  @sales-site-selected=${this._handleSalesSiteChange}
                                              ></der-tm-appointment-booking-date-picker>
                                          </der-ui-progress-bar-item>
                                          <der-ui-progress-bar-item
                                              label="${msg('timepicker.title')}"
                                              .nextButton=${{
                                                  type: 'link',
                                                  clickFn: () => this._isTimePickerValid(),
                                              }}
                                          >
                                              <div class="m-appointment-booking__progress-bar-item-wrapper">
                                                  <div class="m-appointment-booking__selection-wrapper">
                                                      ${
                                                          this.salesSite.name
                                                              ? html`<der-tm-appointment-booking-selected-data>
                                                                    <der-ui-icon-label
                                                                        icon="systemPin"
                                                                        value="${this.salesSite.name}, ${
                                                                            this.salesSite.address.street
                                                                        }, ${this.salesSite.address.zip} ${this.salesSite.address.city}"
                                                                    ></der-ui-icon-label>
                                                                </der-tm-appointment-booking-selected-data>`
                                                              : ``
                                                      }
                                                      <der-tm-appointment-booking-selected-data>
                                                          <der-ui-icon-label
                                                              icon="systemCalendar"
                                                              value=${formatDate(
                                                                  this.selectionData.OpportunityConsultationDate,
                                                                  this.lang
                                                              )}
                                                          ></der-ui-icon-label>
                                                      </der-tm-appointment-booking-selected-data>
                                                  </div>
                                                  <div class="m-appointment-booking__subitem-wrapper">
                                                      <div class="m-appointment-booking__subheadline">
                                                          ${msg('businessComponent.headlines.timepicker')}
                                                      </div>

                                                      <der-tm-appointment-booking-time-picker
                                                          selected-date="${ifDefined(
                                                              this.selectionData.OpportunityConsultationDate
                                                          )}"
                                                          .openingHours=${this.openingHours}
                                                          @der.selected=${this._handleTimePickerSelection}
                                                      ></der-tm-appointment-booking-time-picker>
                                                  </div>
                                              </div>
                                          </der-ui-progress-bar-item>
                                          <der-ui-progress-bar-item
                                              label="${msg('form.title')}"
                                              .nextButton=${{
                                                  type: 'button',
                                                  config: { label: msg('form.submitButton') },
                                                  clickFn: () => this._handleSubmitClick(),
                                              }}
                                          >
                                              <div class="m-appointment-booking__progress-bar-item-wrapper">
                                                  <div class="m-appointment-booking__selection-wrapper">
                                                      ${
                                                          this.salesSite.name
                                                              ? html`<der-tm-appointment-booking-selected-data>
                                                                    <der-ui-icon-label
                                                                        icon="systemPin"
                                                                        value="${this.salesSite.name}, ${
                                                                            this.salesSite.address.street
                                                                        }, ${this.salesSite.address.zip} ${this.salesSite.address.city}"
                                                                    ></der-ui-icon-label>
                                                                </der-tm-appointment-booking-selected-data>`
                                                              : ``
                                                      }
                                                      <der-tm-appointment-booking-selected-data>
                                                          <der-ui-icon-label
                                                              icon="systemCalendar"
                                                              value=${formatDate(
                                                                  this.selectionData.OpportunityConsultationDate,
                                                                  this.lang
                                                              )}
                                                          ></der-ui-icon-label>
                                                      </der-tm-appointment-booking-selected-data>
                                                      <der-tm-appointment-booking-selected-data>
                                                          <der-ui-icon-label
                                                              icon="systemClock"
                                                              value=${`${this.selectionData.OpportunityConsultationTime} ${msg('timepicker.timeUnit')}`}
                                                          ></der-ui-icon-label>
                                                      </der-tm-appointment-booking-selected-data>
                                                  </div>
                                                  <div class="m-appointment-booking__subitem-wrapper">
                                                      <div class="m-appointment-booking__subheadline">
                                                          ${msg('businessComponent.headlines.form')}
                                                      </div>
                                                      <div class="m-appointment-booking__form-wrapper">
                                                          <der-tm-appointment-booking-request-form
                                                              lang="${this.lang}"
                                                              variant="${this.variant}"
                                                              newsletter="${this.newsletter}"
                                                              .dataProtectionLink=${this.dataProtectionLink}
                                                              .formAlert=${this.formAlert}
                                                              @der.submit="${this._handleSubmitClick}"
                                                          ></der-tm-appointment-booking-request-form>
                                                      </div>
                                                  </div>
                                              </div>
                                          </der-ui-progress-bar-item>
                                      </der-ui-progress-bar>
                                  </div>`
                                : html`
                                      <div class="m-appointment-booking__confirmation">
                                          <div class="m-appointment-booking__confirmation-top">
                                              <div class="m-appointment-booking__confirmation-icon">
                                                  <svg
                                                      xmlns="http://www.w3.org/2000/svg"
                                                      width="40"
                                                      height="40"
                                                      viewBox="0 0 40 40"
                                                      fill="none"
                                                      aria-hidden="true"
                                                  >
                                                      <g clip-path="url(#appointment-booking-confirmation-check-clip)">
                                                          <mask
                                                              id="appointment-booking-confirmation-check-mask"
                                                              style="mask-type:alpha"
                                                              maskUnits="userSpaceOnUse"
                                                              x="0"
                                                              y="0"
                                                              width="40"
                                                              height="40"
                                                          >
                                                              <rect width="40" height="40" fill="#D9D9D9" />
                                                          </mask>
                                                          <g mask="url(#appointment-booking-confirmation-check-mask)">
                                                              <path
                                                                  d="M15.8375 29.2991L7.02588 20.4871L8.52588 18.9979L15.8375 26.31L31.4571 10.6796L32.9571 12.1796L15.8375 29.2991Z"
                                                                  fill="white"
                                                              />
                                                          </g>
                                                      </g>
                                                      <defs>
                                                          <clipPath id="appointment-booking-confirmation-check-clip">
                                                              <rect width="40" height="40" fill="white" />
                                                          </clipPath>
                                                      </defs>
                                                  </svg>
                                              </div>
                                              <div class="m-appointment-booking__confirmation-header">
                                                  <div class="m-appointment-booking__confirmation-headline">
                                                      ${msg('confirmation.title')}
                                                  </div>
                                                  <div class="m-appointment-booking__confirmation-text">
                                                      ${unsafeHTML(formatRichText(msg('confirmation.text')))}
                                                  </div>
                                              </div>
                                          </div>

                                          <div class="m-appointment-booking__confirmation-info">
                                              <div class="m-appointment-booking__confirmation-subheadline">
                                                  ${msg('confirmation.subtitle')}
                                              </div>
                                              <div class="m-appointment-booking__selection-wrapper">
                                                  ${
                                                      this.salesSite.name
                                                          ? html`<der-tm-appointment-booking-selected-data>
                                                                <der-ui-icon-label
                                                                    icon="systemPin"
                                                                    value="${this.salesSite.name}, ${
                                                                        this.salesSite.address.street
                                                                    }, ${this.salesSite.address.zip} ${this.salesSite.address.city}"
                                                                ></der-ui-icon-label>
                                                            </der-tm-appointment-booking-selected-data>`
                                                          : ``
                                                  }
                                                  <der-tm-appointment-booking-selected-data>
                                                      <der-ui-icon-label
                                                          icon="systemCalendar"
                                                          value=${formatDate(
                                                              this.selectionData.OpportunityConsultationDate,
                                                              this.lang
                                                          )}
                                                      ></der-ui-icon-label>
                                                  </der-tm-appointment-booking-selected-data>
                                                  <der-tm-appointment-booking-selected-data>
                                                      <der-ui-icon-label
                                                          icon="systemClock"
                                                          value=${`${this.selectionData.OpportunityConsultationTime} ${msg('timepicker.timeUnit')}`}
                                                      ></der-ui-icon-label>
                                                  </der-tm-appointment-booking-selected-data>
                                              </div>
                                          </div>

                                          <div class="m-appointment-booking__confirmation-bottom">
                                              <div class="m-appointment-booking__confirmation-text">
                                                  ${unsafeHTML(formatRichText(msg('confirmation.text2')))}
                                              </div>
                                          </div>
                                      </div>
                                  `
                        }
                    </div>
                </div>
            </div>
        `;
    }
}

if (!customElements.get('der-tm-appointment-booking')) {
    customElements.define('der-tm-appointment-booking', AppointmentBooking);
}
